import { memo, useMemo, useRef, useState, useEffect, useCallback, useLayoutEffect } from 'react'
import type {
  StagedActionEditOptions,
  UpdateStagedActionView,
} from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { ActionTagBadgeCollection } from './badge-collection'
import { EditOptionsProvider } from './edit-options-context'
import { EditableSingleSelect } from './editable-single-select'
import { EditableMultiSelect } from './editable-multi-select'
import { EditableMultiText } from './editable-multi-text'
import { EditableLocalFile } from './editable-local-file'
import { EditableDateTime } from './editable-datetime'
import { twMerge } from 'tailwind-merge'
import { ChevronRight } from 'lucide-react'
import { Button } from '@highlight/ui'

const noop = () => {}

const FADE = '36px'

function getMask(left: boolean, right: boolean): string | undefined {
  if (left && right)
    return `linear-gradient(to right, transparent, black ${FADE}, black calc(100% - ${FADE}), transparent)`
  if (left) return `linear-gradient(to right, transparent, black ${FADE})`
  if (right) return `linear-gradient(to left, transparent, black ${FADE})`
  return undefined
}

interface Tag {
  key: string
  value: string
  icon?: string
  previousValue?: string
  previousIcon?: string
  previousDarkModeIcon?: string
}

interface ActionTagAreaProps {
  tags: Array<Tag>
  tagOrder?: Array<string>
  editOptions?: StagedActionEditOptions | null
  isEditOptionsLoading?: boolean
  isUpdating?: boolean
  canEdit?: boolean
  onUpdate?: (view: UpdateStagedActionView) => void
  className?: string
  showEmpty?: boolean
  singleLine?: boolean
  /** The toolset that executes the action (e.g. `google_calendar`), exposed via
   *  EditOptionsContext so editable fields can resolve provider-specific context. */
  toolSet?: string
}

export const ActionTagArea = memo(function ActionTagArea({
  tags,
  tagOrder,
  editOptions,
  isEditOptionsLoading = false,
  isUpdating = false,
  canEdit = false,
  showEmpty = false,
  onUpdate = noop,
  className,
  singleLine = false,
  toolSet,
}: ActionTagAreaProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const shouldTrackOverflow = singleLine

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    const nextCanScrollLeft = scrollLeft > 0
    const nextCanScrollRight = scrollLeft + clientWidth < scrollWidth - 1
    setCanScrollLeft((prev) => (prev === nextCanScrollLeft ? prev : nextCanScrollLeft))
    setCanScrollRight((prev) => (prev === nextCanScrollRight ? prev : nextCanScrollRight))
  }, [])

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    const amount = el.clientWidth * 0.6
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' })
  }

  const mask = useMemo(
    () => (shouldTrackOverflow ? getMask(canScrollLeft, canScrollRight) : undefined),
    [shouldTrackOverflow, canScrollLeft, canScrollRight],
  )

  // sorts in the following order, to avoid rearranging tags that are already showing
  // 1. tags in the view (sorted by tagOrder)
  // 2. tags that are un-editable and not in the view (sorted by tagOrder)
  // 3. tags that are editable and not in the view (sorted by tagOrder)
  const keys = useMemo(() => {
    const tagKeys = tags.map((tag) => tag.key)
    const editOptionKeys = editOptions?.tags?.map((tag) => tag.key) ?? []

    const sortedTagKeys = tagOrder?.filter((key) => tagKeys.includes(key)) ?? []
    const sortedEditOptionKeys = tagOrder?.filter((key) => editOptionKeys.includes(key)) ?? []
    const orphanedKeys = showEmpty
      ? (tagOrder?.filter((key) => !tagKeys.includes(key) && !editOptionKeys.includes(key)) ?? [])
      : []

    const finalSorted = new Set<string>([
      ...sortedTagKeys,
      ...tagKeys,
      ...orphanedKeys,
      ...sortedEditOptionKeys,
      ...editOptionKeys,
    ])

    return Array.from(finalSorted)
  }, [tagOrder, tags, editOptions, showEmpty])

  // Group tags by key in a single pass (O(tags)) rather than re-scanning the
  // full tag list for every key (O(keys x tags)).
  const tagGroups = useMemo(() => {
    const byKey = new Map<string, Array<Tag>>()
    for (const tag of tags) {
      const existing = byKey.get(tag.key)
      if (existing) existing.push(tag)
      else byKey.set(tag.key, [tag])
    }
    return keys.map((key) => ({ key, tags: byKey.get(key) ?? [] }))
  }, [keys, tags])

  const contextValue = useMemo(
    () => ({
      editOptions: editOptions ?? null,
      viewTags: tags,
      toolSet,
      isEditOptionsLoading,
      isUpdating,
      canEdit,
      onUpdate,
    }),
    [editOptions, tags, toolSet, isEditOptionsLoading, isUpdating, canEdit, onUpdate],
  )

  // Memoized so scroll-state changes (canScrollLeft/Right) don't rebuild the
  // badge/editor element tree.
  const tagContent = useMemo(
    () =>
      tagGroups.map((group) => {
        const editTag = canEdit ? editOptions?.tags?.find((t) => t.key === group.key) : undefined

        if (editTag?.inputType === 'select') {
          return <EditableSingleSelect key={group.key} keyName={group.key} tags={group.tags} editTag={editTag} />
        }

        if (editTag?.inputType === 'multiSelect') {
          return <EditableMultiSelect key={group.key} keyName={group.key} tags={group.tags} editTag={editTag} />
        }

        if (editTag?.inputType === 'multiText') {
          return <EditableMultiText key={group.key} keyName={group.key} tags={group.tags} editTag={editTag} />
        }

        if (editTag?.inputType === 'localFile') {
          return <EditableLocalFile key={group.key} keyName={group.key} tags={group.tags} editTag={editTag} />
        }

        if (editTag?.inputType === 'dateTime') {
          return <EditableDateTime key={group.key} keyName={group.key} tags={group.tags} editTag={editTag} />
        }

        return (
          <ActionTagBadgeCollection key={group.key} keyName={group.key} tags={group.tags} singleLine={singleLine} />
        )
      }),
    [tagGroups, canEdit, editOptions, singleLine],
  )

  useLayoutEffect(() => {
    if (!shouldTrackOverflow) {
      setCanScrollLeft(false)
      setCanScrollRight(false)
      return
    }

    updateOverflow()
  }, [shouldTrackOverflow, tagContent, updateOverflow])

  useEffect(() => {
    if (!shouldTrackOverflow) return
    const el = scrollRef.current
    if (!el) return

    const resizeObserver = new ResizeObserver(updateOverflow)
    resizeObserver.observe(el)

    return () => {
      resizeObserver.disconnect()
    }
  }, [shouldTrackOverflow, updateOverflow])

  return (
    <EditOptionsProvider value={contextValue}>
      <div className={twMerge(singleLine && 'relative -ml-0.5 basis-full')}>
        <div
          ref={scrollRef}
          onScroll={shouldTrackOverflow ? updateOverflow : undefined}
          className={twMerge(
            'flex gap-2',
            singleLine ? 'overflow-x-hidden' : 'flex-wrap',
            !canEdit && 'pointer-events-none',
            className,
          )}
          style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
        >
          {tagContent}
        </div>

        {shouldTrackOverflow && canScrollLeft && (
          <Button
            type="button"
            aria-label="Scroll left"
            size="xs"
            shape="square"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              scroll('left')
            }}
            className="hover:bg-element-hover absolute -left-1 -top-px shadow-none"
          >
            <ChevronRight className="text-foreground-placeholder size-3.5 rotate-180" />
          </Button>
        )}

        {shouldTrackOverflow && canScrollRight && (
          <Button
            type="button"
            aria-label="Scroll right"
            onClick={(e) => {
              e.stopPropagation()
              scroll('right')
            }}
            size="xs"
            shape="square"
            variant="ghost"
            className="hover:bg-element-hover absolute -right-1 -top-px shadow-none"
          >
            <ChevronRight className="text-foreground-placeholder size-3.5" />
          </Button>
        )}
      </div>
    </EditOptionsProvider>
  )
})
